import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

export type ContextMenuItem = {
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  checked?: boolean;
  disabled?: boolean;
  dividerBefore?: boolean;
  submenu?: ContextMenuItem[];
  shortcut?: string;
};

type ContextMenuProps = {
  items: ContextMenuItem[];
  position: { x: number; y: number };
  onClose: () => void;
};

const MENU_CLS = 'fixed z-[10000] min-w-[180px] rounded-md border border-gray-700 bg-gray-800 py-1 shadow-lg shadow-black/30';
const ITEM_CLS_ENABLED = 'text-gray-200 hover:bg-gray-700 hover:text-white';
const ITEM_CLS_DISABLED = 'text-gray-500 cursor-not-allowed';
const DIVIDER_CLS = 'my-1 border-t border-gray-700';
const ICON_CHECK_CLS = 'h-3.5 w-3.5 text-blue-400';
const CHEVRON_CLS = 'h-3.5 w-3.5 text-gray-500';

const SubmenuItem: React.FC<{
  item: ContextMenuItem;
  onClose: () => void;
  focused?: boolean;
}> = ({ item, onClose, focused }) => {
  const [open, setOpen] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const [submenuStyle, setSubmenuStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (!open || !itemRef.current) return;
    const rect = itemRef.current.getBoundingClientRect();
    const submenuWidth = 200;
    const submenuHeight = (item.submenu?.length ?? 0) * 32 + 8;
    const placeLeft = rect.right + submenuWidth > window.innerWidth;
    const x = placeLeft ? rect.left - submenuWidth : rect.right;
    const y = rect.top + submenuHeight > window.innerHeight
      ? window.innerHeight - submenuHeight - 8
      : rect.top;
    setSubmenuStyle({ left: x, top: y });
  }, [open, item.submenu?.length]);

  return (
    <div
      ref={itemRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="relative"
    >
      <button
        type="button"
        role="menuitem"
        className={clsx(
          'flex w-full items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors',
          ITEM_CLS_ENABLED,
          focused && 'bg-gray-700'
        )}
      >
        <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
          {item.icon ?? null}
        </span>
        <span className="flex-1">{item.label}</span>
        {item.shortcut && (
          <span className="ml-auto pl-4 text-xs text-gray-500">{item.shortcut}</span>
        )}
        <ChevronRightIcon className={CHEVRON_CLS} />
      </button>
      {open && item.submenu && createPortal(
        <div
          ref={submenuRef}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          className={clsx(MENU_CLS, 'z-[10001]')}
          style={submenuStyle}
          role="menu"
        >
          {item.submenu.map((sub, idx) => (
            <React.Fragment key={idx}>
              {sub.dividerBefore && <div className={DIVIDER_CLS} role="separator" />}
              <button
                type="button"
                role="menuitem"
                disabled={sub.disabled}
                onClick={() => {
                  sub.onClick?.();
                  onClose();
                }}
                className={clsx(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors',
                  sub.disabled ? ITEM_CLS_DISABLED : ITEM_CLS_ENABLED
                )}
              >
                <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
                  {sub.checked ? <CheckIcon className={ICON_CHECK_CLS} /> : sub.icon ?? null}
                </span>
                <span>{sub.label}</span>
              </button>
            </React.Fragment>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export const ContextMenu: React.FC<ContextMenuProps> = ({ items, position, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjusted, setAdjusted] = useState(position);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const navigableIndices = useMemo(
    () => items.map((item, i) => ({ item, i })).filter(({ item }) => !item.disabled).map(({ i }) => i),
    [items]
  );

  // Reset focused index when menu position changes (i.e. menu re-opens)
  useEffect(() => { setFocusedIndex(-1); }, [position]);

  useEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = position.x + rect.width > window.innerWidth ? window.innerWidth - rect.width - 8 : position.x;
    const y = position.y + rect.height > window.innerHeight ? window.innerHeight - rect.height - 8 : position.y;
    setAdjusted({ x, y });
  }, [position]);
  const focusedIndexRef = useRef(focusedIndex);
  useEffect(() => { focusedIndexRef.current = focusedIndex; }, [focusedIndex]);

  useEffect(() => {
    const handleDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking inside a portaled submenu (role="menu")
      if (target.closest?.('[role="menu"]')) return;
      if (menuRef.current && !menuRef.current.contains(target)) onClose();
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setFocusedIndex((prev) => {
          const currentPos = navigableIndices.indexOf(prev);
          const nextPos = currentPos < navigableIndices.length - 1 ? currentPos + 1 : 0;
          return navigableIndices[nextPos] ?? -1;
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setFocusedIndex((prev) => {
          const currentPos = navigableIndices.indexOf(prev);
          const nextPos = currentPos > 0 ? currentPos - 1 : navigableIndices.length - 1;
          return navigableIndices[nextPos] ?? -1;
        });
        return;
      }
      if (e.key === 'Enter' && focusedIndexRef.current >= 0) {
        e.preventDefault();
        e.stopPropagation();
        const item = items[focusedIndexRef.current];
        if (item && !item.disabled && !item.submenu) {
          item.onClick?.();
          onClose();
        }
        return;
      }
    };
    document.addEventListener('mousedown', handleDown, true);
    document.addEventListener('keydown', handleKey, true);
    return () => {
      document.removeEventListener('mousedown', handleDown, true);
      document.removeEventListener('keydown', handleKey, true);
    };
  }, [onClose, navigableIndices, items]);
  return createPortal(
    <div
      ref={menuRef}
      className={MENU_CLS}
      style={{ left: adjusted.x, top: adjusted.y }}
      role="menu"
    >
      {items.map((item, idx) => (
        <React.Fragment key={idx}>
          {item.dividerBefore && <div className={DIVIDER_CLS} role="separator" />}
          {item.submenu ? (
            <SubmenuItem item={item} onClose={onClose} focused={idx === focusedIndex} />
          ) : (
            <button
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                item.onClick?.();
                onClose();
              }}
              className={clsx(
                'flex w-full items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors',
                item.disabled ? ITEM_CLS_DISABLED : ITEM_CLS_ENABLED,
                idx === focusedIndex && 'bg-gray-700'
              )}
            >
              <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
                {item.checked ? <CheckIcon className={ICON_CHECK_CLS} /> : item.icon ?? null}
              </span>
              <span className="flex-1">{item.label}</span>
              {item.shortcut && (
                <span className="ml-auto pl-4 text-xs text-gray-500">{item.shortcut}</span>
              )}
            </button>
          )}
        </React.Fragment>
      ))}
    </div>,
    document.body
  );
};

type UseContextMenuReturn = {
  open: boolean;
  position: { x: number; y: number };
  handleContextMenu: (e: React.MouseEvent) => void;
  close: () => void;
};

// eslint-disable-next-line react-refresh/only-export-components -- hook paired with ContextMenu component in this file
export const useContextMenu = (): UseContextMenuReturn => {
  const [state, setState] = useState<{ open: boolean; position: { x: number; y: number } }>({
    open: false,
    position: { x: 0, y: 0 },
  });
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setState({ open: true, position: { x: e.clientX, y: e.clientY } });
  }, []);
  const close = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);
  return { open: state.open, position: state.position, handleContextMenu, close };
};
